"use client";

import { useSelection } from "@frend-digital/centra/client";
import { sendGTMEvent } from "@frend-digital/events/gtm";

const sha256Hash = async (text: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
};

const USER_DATA_KEY = "events_user_data";

export interface UserData {
  user_data?: {
    email?: string;
    sha256_hashed_email?: string;
    phone_number?: string;
    sha256_hashed_phone_number?: string;
    first_name?: string;
    sha256_hashed_first_name?: string;
    last_name?: string;
    sha256_hashed_last_name?: string;
    street?: string;
    sha256_hashed_street?: string;
    city?: string;
    sha256_hashed_city?: string;
    region?: string;
    sha256_hashed_region?: string;
    country?: string;
    sha256_hashed_country?: string;
    postal_code?: string;
    sha256_hashed_postal_code?: string;
  };
}

export const getUserData = (): UserData => {
  if (typeof window === "undefined") {
    console.error("Attempted to call getUserData called on server side");
    return {};
  }
  const userData = JSON.parse(
    localStorage.getItem(USER_DATA_KEY) || "{}",
  ) as UserData;
  return userData;
};

export const setUserData = async (userData: UserData) => {
  const oldUserData = getUserData();

  const newUserData = { ...oldUserData.user_data, ...userData.user_data };

  if (newUserData.email) {
    newUserData.sha256_hashed_email = await sha256Hash(newUserData.email);
  }
  if (newUserData.phone_number) {
    newUserData.sha256_hashed_phone_number = await sha256Hash(
      newUserData.phone_number,
    );
  }
  if (newUserData.first_name) {
    newUserData.sha256_hashed_first_name = await sha256Hash(
      newUserData.first_name,
    );
  }
  if (newUserData.last_name) {
    newUserData.sha256_hashed_last_name = await sha256Hash(
      newUserData.last_name,
    );
  }
  if (newUserData.street) {
    newUserData.sha256_hashed_street = await sha256Hash(newUserData.street);
  }
  if (newUserData.city) {
    newUserData.sha256_hashed_city = await sha256Hash(newUserData.city);
  }
  if (newUserData.region) {
    newUserData.sha256_hashed_region = await sha256Hash(newUserData.region);
  }
  if (newUserData.country) {
    newUserData.sha256_hashed_country = await sha256Hash(newUserData.country);
  }
  if (newUserData.postal_code) {
    newUserData.sha256_hashed_postal_code = await sha256Hash(
      newUserData.postal_code,
    );
  }

  localStorage.setItem(
    USER_DATA_KEY,
    JSON.stringify({
      user_data: newUserData,
    }),
  );

  sendGTMEvent({
    event: "user_data_updated",
    user_data: newUserData,
  });

  return newUserData;
};

export const useUserData = () => {
  const { data: centra } = useSelection();

  const userData: UserData = {};

  if (centra?.selection) {
    const address = centra.selection.address;
    const user = centra.loggedIn;

    const email = address?.email || user?.email;
    const phoneNumber = address?.phoneNumber;
    const address1 = address?.address1 || user?.address1;
    const city = address?.city || user?.city;
    const state = address?.state || user?.state;
    const country = address?.country || user?.country;
    const zipCode = address?.zipCode || user?.zipCode;
    const firstName = address?.firstName || user?.firstName;
    const lastName = address?.lastName || user?.lastName;

    if (email) {
      userData.user_data ??= {};
      userData.user_data.email = email;
    }

    if (phoneNumber) {
      userData.user_data ??= {};
      userData.user_data.phone_number = phoneNumber;
    }

    if (address1) {
      userData.user_data ??= {};
      userData.user_data.street = address1;
    }

    if (city) {
      userData.user_data ??= {};
      userData.user_data.city = city;
    }

    if (state) {
      userData.user_data ??= {};
      userData.user_data.region = state;
    }

    if (country) {
      userData.user_data ??= {};
      userData.user_data.country = country;
    }

    if (zipCode) {
      userData.user_data ??= {};
      userData.user_data.postal_code = zipCode;
    }

    if (firstName) {
      userData.user_data ??= {};
      userData.user_data.first_name = firstName;
    }

    if (lastName) {
      userData.user_data ??= {};
      userData.user_data.last_name = lastName;
    }
  }

  return userData;
};
